import { useEffect } from 'react';

const SITE_URL = 'https://marine-sealink.com';

/**
 * Custom hook for setting page-specific SEO meta tags and structured data.
 * @param {Object} options - SEO options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.canonical - Canonical URL path (e.g., '/electrical-spares')
 * @param {string} options.ogImage - Open Graph image URL (optional)
 * @param {Array<{name: string, url: string}>} options.breadcrumb - Breadcrumb trail for BreadcrumbList JSON-LD (optional)
 * @param {Array<{question: string, answer: string}>} options.faqs - FAQs for FAQPage JSON-LD (optional)
 */
const useSEO = ({ title, description, canonical, ogImage, breadcrumb, faqs }) => {
  useEffect(() => {
    // Set document title
    const fullTitle = title ? `${title} | Marine Sealink B.V.` : 'Marine Sealink B.V. | Precision Automation & Control Systems Spares';
    document.title = fullTitle;

    // Helper function to update or create meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set meta description
    if (description) {
      setMetaTag('description', description);
      setMetaTag('og:description', description, true);
      setMetaTag('twitter:description', description);
    }

    // Set Open Graph tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('twitter:title', fullTitle);

    // Set canonical URL
    if (canonical) {
      const fullCanonical = `${SITE_URL}${canonical}`;
      setMetaTag('og:url', fullCanonical, true);

      // Update or create canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', fullCanonical);
    }

    // Set OG image if provided
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('twitter:image', ogImage);
    }

    // Build per-page structured data (BreadcrumbList + FAQPage)
    const graph = [];
    if (breadcrumb && breadcrumb.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: `${SITE_URL}${crumb.url}`,
        })),
      });
    }
    if (faqs && faqs.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      });
    }

    // Remove any stale page JSON-LD, then inject the current page's graph.
    document
      .querySelectorAll('script[data-page-jsonld]')
      .forEach((el) => el.remove());

    let scriptEl = null;
    if (graph.length > 0) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.setAttribute('data-page-jsonld', 'true');
      scriptEl.text = JSON.stringify(graph.length === 1 ? graph[0] : graph);
      document.head.appendChild(scriptEl);
    }

    // Cleanup: reset title and remove the injected per-page JSON-LD.
    return () => {
      document.title = 'Marine Sealink B.V. | Precision Automation & Control Systems Spares';
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, canonical, ogImage, breadcrumb, faqs]);
};

export default useSEO;
